import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { PRICE_TEXT } from "../content/productFacts";
import { siteGraph } from "../util/siteSchema";

const BASE_URL = "https://shifttracker.net";
// Opaque 1200x630 JPEG for social previews. Transparent PNGs and large files
// (logo.png is 1024x1024 RGBA, ~1.4MB) fail to render on WhatsApp/Twitter.
const OG_IMAGE_URL = `${BASE_URL}/og-image.jpg`;

type Lang = "tr" | "en";

interface SeoContent {
  title: string;
  description: string;
}

// Language-neutral path ("/", "/about", "/contact") -> per-language meta.
const SEO_CONTENT: Record<string, Record<Lang, SeoContent>> = {
  "/": {
    tr: {
      title: "Tam Otomatik Akaryakıt İstasyonu Yönetimi - Shift Tracker",
      description:
        "Tam otomatik, modern akaryakıt istasyonu yönetimi: vardiya bir dakikada kapanır, kart satışları bankayla eşleşir, nakit yatırmalar aynı gün doğrulanır.",
    },
    en: {
      title: "Fully Automated Fuel Station Management - Shift Tracker",
      description:
        "Fully automated, modern fuel station management: close a shift in under a minute, match card sales to bank payments and verify cash deposits the same day.",
    },
  },
  "/about": {
    tr: {
      title:
        "Hakkımızda | İstasyoncular İçin Geliştirildi - Shift Tracker",
      description:
        "Shift Tracker'ı kendi akaryakıt istasyonumuz için geliştirdik: banka onaylı vardiya mutabakatı bir dakikada. Güvene değil, kanıta dayalı istasyon yönetimi.",
    },
    en: {
      title: "About | Built by Fuel-Station Operators - Shift Tracker",
      description:
        "We built Shift Tracker for our own fuel station: hours of shift reconciliation became a one-minute, bank-verified close. Management based on proof, not trust.",
    },
  },
  "/contact": {
    tr: {
      title: "İletişim | Akaryakıt Yazılımı Demo Talebi - Shift Tracker",
      description:
        "Akaryakıt ve benzin istasyonu nakit akış yönetimi yazılımı Shift Tracker hakkında bilgi almak ve demo talep etmek için bizimle iletişime geçin.",
    },
    en: {
      title: "Contact | Request Fuel Station Software Demo - Shift Tracker",
      description:
        "Get in touch to learn more and request a demo of Shift Tracker, the cash flow management software for fuel and gas stations.",
    },
  },
  "/fraud-prevention": {
    tr: {
      title:
        "Akaryakıt İstasyonu Dolandırıcılık Önleme | Shift Tracker",
      description:
        "Mükerrer dekont, nakit gösterilen kart satışı ve ön ödemeli kart manipülasyonu gibi açıkları banka onaylı mutabakat ve denetim iziyle kapatın.",
    },
    en: {
      title: "Gas Station Fraud Detection & Prevention | Shift Tracker",
      description:
        "Close fuel station fraud gaps such as duplicate receipts, card sales disguised as cash and prepaid card abuse with bank-proven reconciliation and audit trails.",
    },
  },
  "/fiyatlandirma": {
    tr: {
      title:
        "Fiyatlandırma | Akaryakıt İstasyonu Yazılımı - Shift Tracker",
      description:
        `Shift Tracker fiyatları: aylık ${PRICE_TEXT.tr.monthly}, yıllık ${PRICE_TEXT.tr.annual} (KDV dahil, sınırlı süreli indirim). TURPAK entegrasyonu ve banka onaylı mutabakat dahil.`,
    },
    en: {
      title: "Pricing | Gas Station Software Plans - Shift Tracker",
      description:
        `Shift Tracker pricing: ${PRICE_TEXT.eng.monthly}/month or ${PRICE_TEXT.eng.annual}/year (VAT included, limited-time discount). TURPAK integration and bank-verified reconciliation included.`,
    },
  },
};

// All canonical/hreflang URLs use a trailing slash to match what Cloudflare Pages
// actually serves (react-snap emits directory-index files, so `/about`
// 301-redirects to `/about/`). Keeping these in sync avoids "Page with
// redirect" in Search Console and canonical-vs-served-URL mismatches.
// `path` is the Turkish (root) path; `enPath` is the English path when its slug
// differs (e.g. tr "/akaryakit-istasyonu-yazilimi" ↔ en "/gas-station-software").
// When omitted, the English page uses the same slug under /en/.
function languageUrls(path: string, enPath?: string): { tr: string; en: string } {
  const enP = enPath ?? path;
  const tr = path === "/" ? `${BASE_URL}/` : `${BASE_URL}${path}/`;
  const en = enP === "/" ? `${BASE_URL}/en/` : `${BASE_URL}/en${enP}/`;
  return { tr, en };
}

interface SeoProps {
  // Turkish (root) path of the page, e.g. "/", "/about", "/blog/<tr-slug>".
  path: string;
  // English path when its slug differs from the Turkish one (e.g.
  // "/gas-station-software" or "/blog/<en-slug>"). Defaults to `path`.
  enPath?: string;
  // Optional overrides for pages not in SEO_CONTENT (e.g. blog posts).
  title?: string;
  description?: string;
  // og:type, "website" (default) or "article" for blog posts.
  type?: "website" | "article";
  // Extra JSON-LD payload (e.g. a BlogPosting) emitted alongside the page.
  schema?: object | null;
}

function Seo({
  path,
  enPath,
  title,
  description,
  type = "website",
  schema = null,
}: SeoProps): JSX.Element {
  const location = useLocation();
  const isEnglish =
    location.pathname === "/en" || location.pathname.startsWith("/en/");
  const lang: Lang = isEnglish ? "en" : "tr";

  const fallback = SEO_CONTENT[path]?.[lang];
  const content: SeoContent = {
    title: title ?? fallback?.title ?? "Shift Tracker",
    description: description ?? fallback?.description ?? "",
  };
  const { tr: trUrl, en: enUrl } = languageUrls(path, enPath);
  const canonical = lang === "en" ? enUrl : trUrl;
  const homeUrl = lang === "en" ? `${BASE_URL}/en/` : `${BASE_URL}/`;

  // Breadcrumb trail for non-home pages: Home > Current page, and
  // Home > Blog > Article for blog posts.
  const crumbs = [
    { name: lang === "en" ? "Home" : "Ana Sayfa", item: homeUrl },
    ...(path.startsWith("/blog/")
      ? [{ name: "Blog", item: `${homeUrl}blog/` }]
      : []),
    { name: content.title, item: canonical },
  ];
  const breadcrumbSchema =
    path === "/"
      ? null
      : {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: crumbs.map((crumb, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: crumb.name,
            item: crumb.item,
          })),
        };

  return (
    <Helmet defer={false}>
      <html lang={lang} />
      <title>{content.title}</title>
      <meta name="description" content={content.description} />
      <link rel="canonical" href={canonical} />

      <link rel="alternate" hrefLang="tr" href={trUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={trUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Shift Tracker" />
      <meta
        property="og:locale"
        content={lang === "en" ? "en_US" : "tr_TR"}
      />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={content.title} />
      <meta property="og:description" content={content.description} />
      <meta property="og:image" content={OG_IMAGE_URL} />
      <meta property="og:image:secure_url" content={OG_IMAGE_URL} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Shift Tracker" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={content.title} />
      <meta name="twitter:description" content={content.description} />
      <meta name="twitter:image" content={OG_IMAGE_URL} />
      <meta name="twitter:image:alt" content="Shift Tracker" />

      {/* Organization, website, software and this page (src/util/siteSchema.ts). */}
      <script type="application/ld+json">
        {JSON.stringify(
          siteGraph(lang, {
            url: canonical,
            title: content.title,
            description: content.description,
          })
        )}
      </script>
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
}

export default Seo;
